import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function Login() {
  const { login, user, isStaff } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isStaff) navigate("/admin", { replace: true });
    else if (user?.role === "partenaire") navigate("/partenaire", { replace: true });
    else if (user) navigate("/", { replace: true });
  }, [user, isStaff, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (["patron", "vendeur", "associe"].includes(u.role)) navigate("/admin", { replace: true });
      else if (u.role === "partenaire") navigate("/partenaire", { replace: true });
      else navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "border border-brand-line rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all bg-white text-brand-ink";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-cream">
      <div className="hidden lg:flex relative flex-col justify-between p-12" style={{ background: 'linear-gradient(160deg, #2C2550 0%, #191338 100%)' }}>
        <div className="flex items-center gap-2 text-white">
          <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><img src="/noumabou-emblem.png" alt="" className="w-7 h-7 object-contain" /></span>
          <span className="font-heading font-extrabold text-2xl">NOUMABOU</span>
        </div>
        <div className="text-white">
          <p className="font-heading text-4xl font-black leading-tight">Espace de gestion</p>
          <p className="text-white/60 mt-3 max-w-sm">Stock, ventes, livraisons, paiements et bénéfices — en temps réel, où que vous soyez.</p>
          <p className="text-brand/80 mt-6 text-sm font-semibold tracking-wide">Nourrir l'âme, soigner le corps.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <span className="w-10 h-10 rounded-lg bg-brand-ink flex items-center justify-center"><img src="/noumabou-emblem.png" alt="" className="w-7 h-7 object-contain" /></span>
            <span className="font-heading font-extrabold text-2xl text-brand-ink">NOUMABOU</span>
          </div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Connexion</h1>
          <p className="text-brand-muted mt-1">Accédez à votre espace privé.</p>

          <form onSubmit={submit} className="space-y-4 mt-8">
            <div>
              <label className="text-sm font-medium text-brand-sub">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " mt-1"} placeholder="vous@noumabou.org" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-sub">Mot de passe</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + " mt-1"} placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-brand-secondary">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-brand text-white hover:bg-brand-hover transition-colors rounded-full px-6 py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Se connecter</>}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-brand-line" />
            <span className="text-xs text-brand-muted">ou</span>
            <div className="flex-1 h-px bg-brand-line" />
          </div>

          <button onClick={googleLogin} data-testid="google-login" className="w-full bg-white border border-brand-line hover:bg-brand-cream transition-colors rounded-full px-6 py-3.5 font-semibold flex items-center justify-center gap-3 text-brand-ink">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuer avec Google
          </button>

          <Link to="/" className="block text-center text-sm text-brand-muted mt-6 hover:text-brand">← Retour à la boutique</Link>
        </div>
      </div>
    </div>
  );
}
